import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../utils/styledComponent';
import theme from '../theme';
import { formatClockTime } from '../utils/displayFormat';
import Button from './atoms/forms/Button';

const StyledDiv = styledComponent('div', {
  '& .fb-card': {
    background: theme.colors.bgMuted,
    borderRadius: theme.radii.card,
    border: theme.borders.card,
    padding: theme.spacing.cardPadding,
  },
  '& .fb-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.fieldGap,
    gap: theme.spacing.gapMd,
    flexWrap: 'wrap',
  },
  '& .fb-title': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
    ...theme.typography.sectionHeader,
    textTransform: 'uppercase',
    color: theme.colors.textMuted,
    letterSpacing: '0.05em',
  },
  '& .fb-title-icon': {
    color: theme.colors.textHint,
    display: 'inline-flex',
  },
  '& .fb-clear': {
    background: 'none',
    border: 'none',
    color: theme.colors.primary,
    cursor: 'pointer',
    ...theme.typography.body,
    textDecoration: 'underline',
    borderRadius: theme.radii.input,
    padding: theme.spacing.gapXs,
    '&:hover': {
      color: theme.colors.primaryHover,
    },
    '&:focus': {
      outline: 'none',
      boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
    },
  },
  '& .fb-row': {
    display: 'flex',
    gap: theme.spacing.sectionGap,
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  '& .fb-children': {
    flex: 1,
    display: 'flex',
    gap: theme.spacing.sectionGap,
    flexWrap: 'wrap',
  },
  '& .fb-apply-wrap': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing.gapSm,
  },
  '& .fb-last-applied': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    whiteSpace: 'nowrap',
  },
  '& .fb-last-applied[data-applying="true"]': {
    fontStyle: 'italic',
    color: theme.colors.textHint,
  },
  [`@media (max-width: ${theme.breakpoints.mobile})`]: {
    '& .fb-card': {
      padding: theme.spacing.bodyPadding,
    },
    '& .fb-row': {
      alignItems: 'stretch',
    },
    '& .fb-children': {
      flexDirection: 'column',
      flexBasis: '100%',
      gap: theme.spacing.fieldGap,
    },
    '& .fb-apply-wrap': {
      width: '100%',
    },
    '& .fb-apply-wrap > button': {
      width: '100%',
    },
  },
});

const SearchIcon = () => (
  <svg
    className="fb-title-icon"
    width={theme.sizes.icon}
    height={theme.sizes.icon}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="5" />
    <line x1="11" y1="11" x2="14" y2="14" />
  </svg>
);

function FilterBar({
  title,
  onApply,
  onClearAll,
  applyDisabled,
  isApplying,
  lastAppliedAt,
  children,
}) {
  const statusText = (() => {
    if (isApplying) return 'Refreshing data…';
    if (lastAppliedAt)
      return `Last applied at ${formatClockTime(lastAppliedAt, {
        seconds: true,
      })}`;
    return '';
  })();

  return (
    <StyledDiv>
      <div className="fb-card">
        <div className="fb-header">
          <span className="fb-title">
            <SearchIcon />
            {title}
          </span>
          <button
            type="button"
            className="fb-clear"
            onClick={onClearAll}
            data-testid="filter-bar-clear"
          >
            Clear All Filters
          </button>
        </div>
        <div className="fb-row">
          <div className="fb-children">{children}</div>
          <div className="fb-apply-wrap">
            <Button
              variant="primary"
              onClick={onApply}
              disabled={applyDisabled}
              isLoading={isApplying}
              loadingText="Applying…"
              dataTestId="filter-bar-apply"
            >
              Apply Filters
            </Button>
            {statusText && (
              <span
                className="fb-last-applied"
                data-applying={isApplying ? 'true' : 'false'}
                data-testid="filter-bar-status"
              >
                {statusText}
              </span>
            )}
          </div>
        </div>
      </div>
    </StyledDiv>
  );
}

FilterBar.propTypes = {
  title: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  applyDisabled: PropTypes.bool,
  isApplying: PropTypes.bool,
  lastAppliedAt: PropTypes.instanceOf(Date),
  children: PropTypes.node.isRequired,
};

FilterBar.defaultProps = {
  title: 'Filter Data',
  applyDisabled: false,
  isApplying: false,
  lastAppliedAt: null,
};

export default FilterBar;
